import { defineStore } from 'pinia';
import {
  addItemUseCase, applyPromoCodeUseCase, checkoutUseCase,
  createCheckoutUseCase,
  fetchScheduleUseCase, getCheckoutUseCase, pollUseCase, removeItemUseCase, setCommentUseCase, setRequestsUseCase,
  setServiceUseCase, setShareRequestsUseCase, setTimeZoneUseCase
} from "#sharedMarketplace/features/checkout/useCases/checkoutUseCase";
import {useCheckoutPaymentRedirect} from "#sharedMarketplace/features/checkout/composables/useCheckoutPaymentRedirect";

const pollInterval = ref();

export const useCheckoutStore = defineStore(
  'marketplace/checkout',
  () => {
    const isLoading = ref(false);
    const isCheckoutLoading = ref(false);
    const formData = ref({
      id: null,
      psychologist: null,
      serviceId: null,
      items: [],
      comment: null,
      features: {
        registration: false,
        showPrices: false,
        serviceSelector: false,
        promoCodes: false
      },
      shareRequests: true,
      messages: [],
    });
    const schedule = ref([]);
    const paymentData = ref({
      invoiceId: null,
      status: 'created',
      state: null,
      url: null,
      embedded: false,
      total: 0,
      grandTotal: 0,
      currency: 'UAH',
      timeZone: null,
      items: [],
    });
    const requests = ref([]);

    const initCheckout = async (psychologistSlug: string, serviceId?: string) => {
      try {
        isLoading.value = true;
        clear();
        const checkout = await createCheckoutUseCase(psychologistSlug, Intl.DateTimeFormat().resolvedOptions().timeZone, serviceId);
        if (checkout.id) {
          formData.value = checkout;

          if (formData.value?.serviceId) {
            await fetchSchedule();
          }
        } else {
          formData.value.messages = checkout.messages;
        }
      } catch (e) {
        console.error(e);
      } finally {
        isLoading.value = false;
      }
    }

    const createCheckout = async (psychologistSlug: string, serviceId?: string) => {
      if (!formData.value?.id) {
        await initCheckout(psychologistSlug, serviceId);
      } else {
        await fetchCheckout(formData.value.id);

        if (formData.value?.psychologist !== psychologistSlug || (serviceId && formData.value?.serviceId !== serviceId)) {
          await initCheckout(psychologistSlug, serviceId);
        }
      }
    }

    const fetchCheckout = async (id: string) => {
      try {
        isLoading.value = true;
        formData.value = await getCheckoutUseCase(id);

        if (formData.value?.serviceId) {
          await fetchSchedule();
        }
      } catch (e) {
        console.error(e);
      } finally {
        isLoading.value = false;
      }
    }

    const setService = async (serviceId: string) => {
      if (formData.value?.id) {
        try {
          isLoading.value = true;
          formData.value = await setServiceUseCase(formData.value.id, serviceId);
          await fetchSchedule();
        } catch (e) {
          console.error(e);
        } finally {
          isLoading.value = false;
        }
      }
    }

    const setTimeZone = async (timeZone: string) => {
      if (formData.value?.id) {
        try {
          isLoading.value = true;
          formData.value = await setTimeZoneUseCase(formData.value.id, timeZone);
        } catch (e) {
          console.error(e);
        } finally {
          isLoading.value = false;
        }
      }
    }

    const setShareRequests = async (shareRequests: boolean) => {
      if (formData.value?.id) {
        try {
          isLoading.value = true;
          formData.value = await setShareRequestsUseCase(formData.value.id, shareRequests);
        } catch (e) {
          console.error(e);
        } finally {
          isLoading.value = false;
        }
      }
    }

    const fetchSchedule = async () => {
        if (formData.value?.id && formData.value?.serviceId) {
          try {
            isLoading.value = true;
            schedule.value = await fetchScheduleUseCase(formData.value.id);
          } catch (e) {
            console.error(e);
          } finally {
            isLoading.value = false;
          }
        }
    }

    const addItem = async (timeSlot: string) => {
        if (formData.value?.id && formData.value?.serviceId) {
          isLoading.value = true;
          try {
            formData.value = await addItemUseCase(formData.value.id, timeSlot);
          } catch (e) {
            console.error(e);
          } finally {
            isLoading.value = false;
          }
        }
    }

    const removeItem = async (itemId: string) => {
        if (formData.value?.id && formData.value?.serviceId) {
          isLoading.value = true;
          try {
            formData.value = await removeItemUseCase(formData.value.id, itemId);
          } catch (e) {
            console.error(e);
          } finally {
            isLoading.value = false;
          }
        }
    }

    const applyPromoCode = async (promoCode: string) => {
        if (formData.value?.id && formData.value?.serviceId) {
          isLoading.value = true;
          try {
            formData.value = await applyPromoCodeUseCase(formData.value.id, promoCode);
          } catch (e) {
            console.error(e);
          } finally {
            isLoading.value = false;
          }
        }
    }

    const checkout = async (checkoutId: string, matchingId: string|undefined) => {
      try {
        if (formData.value.comment) {
          await setCommentUseCase(checkoutId, formData.value.comment);
        }

        paymentData.value = await checkoutUseCase(checkoutId, requests.value, matchingId);
      } catch (e) {
        if (e.statusCode === 404) {
          throw e;
        }
      }
    }

    const poll = async (checkoutId: string) => {
      const {handle} = useCheckoutPaymentRedirect();

      stopPolling();

      pollInterval.value = setInterval(async () => {
        paymentData.value = await pollUseCase(checkoutId);
        handle();
      }, 3000)
    }

    const stopPolling = () => {
      if (pollInterval.value) {
        clearInterval(pollInterval.value);
      }
    }

    const clear = () => {
      formData.value = {
        id: null,
        serviceId: null,
        items: [],
        shareRequests: true,
        features: {
          registration: false,
          showPrices: false,
          serviceSelector: false,
          promoCodes: false,
        }
      };
      schedule.value = [];
      paymentData.value = {
        invoiceId: null,
        state: null,
        url: null,
        embedded: false,
        total: 0,
        grandTotal: 0,
        currency: 'UAH',
        timeZone: null,
        items: [],
      };
      requests.value = [];
    }

    return {
      isLoading,
      isCheckoutLoading,
      formData,
      schedule,
      requests,
      paymentData,
      createCheckout,
      fetchCheckout,
      setService,
      setTimeZone,
      setShareRequests,
      fetchSchedule,
      addItem,
      removeItem,
      applyPromoCode,
      checkout,
      poll,
      stopPolling,
      clear,
    }
  },
  {
    persist: {
      pick: [
        'formData.currency',
        'formData.comment',
        'formData.discountPercent',
        'formData.discountTotal',
        'formData.features',
        'formData.grandTotal',
        'formData.id',
        'formData.items',
        'formData.promoCode',
        'formData.psychologist',
        'formData.requests',
        'formData.serviceId',
        'formData.shareRequests',
        'formData.timeZone',
        'formData.total',
      ],
      storage: piniaPluginPersistedstate.cookies({
        maxAge: 60 * 60
      }),
    }
  }
)
